/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable object-shorthand */
/* eslint-disable react-hooks/exhaustive-deps */
import { CubeTransparentIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { NewspaperIcon, PresentationChartBarIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Checkbox,
  Menu,
  Paper, Text,
} from "@mantine/core";
import React, {
  useEffect,
  useMemo, useRef, useState,
} from "react";
import uuid from "react-uuid";
import { ReactComponent as He } from "../../../assets/images/heSm.svg";
import { ReactComponent as Segment } from "../../../assets/images/segmentAvatar.svg";
import { IconWorld } from "@tabler/icons-react";
import { ChevronDownIcon, Type } from "lucide-react";

type Props = {
  list?: any[];
  metricsList?: any[];
  selected: any;
  setSelected: any;
  type?: string;
  children?: React.ReactNode;
  isOpen: any;
  setIsOpen: any;
  position?: "bottom" | "left" | "right" | "top" | "bottom-end" | "bottom-start" | "left-end" | "left-start" | "right-end" | "right-start" | "top-end" | "top-start";
};

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const listOfSources = [
  {
    name: "all",
    label: "All Sources",
    icon: CubeTransparentIcon,
  },
  {
    name: "native",
    label: "Hyperengage",
    icon: He,
  },
  {
    name: "segment",
    label: "Segment",
    icon: Segment,
  },
];

const listOfAttributes = [
  {
    name: "all",
    label: "All",
    icon: CubeTransparentIcon,
  },
  {
    name: "traits",
    label: "Traits",
    icon: NewspaperIcon,
  },
  {
    name: "metrics",
    label: "Metrics",
    icon: PresentationChartBarIcon,
  },
];

const MultiSelectDropdown = ({
  list = [],
  metricsList = [],
  isOpen = false,
  setIsOpen,
  children,
  type = "Accounts",
  selected,
  setSelected,
  position = "left-start",
}: Props) => {
  const [query, setQuery] = useState<string>("");
  const [selectedSource, setSelectedSource] = useState(listOfSources[0].name);
  const [selectedAttribute, setSelectedAttribute] = useState<any>("all");
  const [queriedData, setQueriedData] = useState<any[]>([]);

  const displayTraits = useMemo(() => (selectedSource === "all"
    ? list?.filter((item: any) => item?.name !== "accounts.name" && item?.name !== "users.name") : list?.filter((item: any) => item?.source === selectedSource && item?.name !== "accounts.name" && item?.name !== "users.name")
  ), [selectedSource, list]);

  const displayMetrics = useMemo(() => (selectedSource === "all"
    ? metricsList : metricsList?.filter(
      (item: any) => item?.source === selectedSource,
    )), [selectedSource, metricsList]);

  useEffect(() => {
    if (displayMetrics.length || displayTraits.length) {
      let data = [];
      if (selectedAttribute === "metrics") {
        data = displayMetrics.filter(
          (item) => item?.display_name?.toLowerCase().includes(query.toLowerCase()),
        );
      }
      if (selectedAttribute === "traits") {
        data = displayTraits.filter(
          (item) => item?.display_name?.toLowerCase().includes(query.toLowerCase()),
        );
      }
      if (selectedAttribute === "all") {
        data = [...displayTraits, ...displayMetrics].filter(
          (item) => item?.display_name?.toLowerCase().includes(query.toLowerCase()),
        );
      }
      setQueriedData(data);
    }
    return () => {
      setQueriedData([]);
    };
  }, [query, selectedAttribute, displayMetrics.length, displayTraits.length]);

  const isOpenRef = useRef(isOpen); // Create a ref for the isOpen state
  isOpenRef.current = isOpen; // Update the ref whenever isOpen changes

  return (
    <Menu
      withinPortal
      closeOnClickOutside
      opened={isOpen}
      onChange={setIsOpen}
      position={position}
      shadow="md"
    >
      <Menu.Target>
        {children}
      </Menu.Target>
      <Menu.Dropdown className="z-10 p-0 origin-bottom-right">
        <div className="h-[300px] w-[320px] overflow-hidden flex flex-col rounded-md border border-gray-200">
          {/* Search bar with filter buttons */}
          <div className="flex items-center border-b border-gray-200 p-2">
            <div className="relative flex-1">
              <div
                className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                aria-hidden="true"
              >
                <MagnifyingGlassIcon
                  className="h-3.5 w-3.5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                value={query}
                type="text"
                className="text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-xs text-gray-600 w-full pl-9 pr-3 py-1.5 focus:text-gray-900"
                placeholder={`Search ${type}`}
              />
            </div>
          </div>
          
          {/* Filter panel */}
          <div className="border-b border-gray-200 bg-gray-50 p-2">
            <div className="flex justify-between items-center mb-2">
              <div className="flex space-x-1">                
                <div className="relative">
                  <Menu position="bottom-start" offset={4} withinPortal={false}>
                    <Menu.Target>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconWorld className="h-3 w-3" />}
                        className="text-xs font-medium flex items-center space-x-1"
                        rightSection={<ChevronDownIcon className="h-3 w-3" />}
                      >
                        <span>{listOfSources.find(item => item.name === selectedSource)?.label}</span>
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown className="p-1 min-w-[120px] bg-gray-50">
                      {listOfSources.map((source) => (
                        <Menu.Item
                          key={source.name}
                          leftSection={<source.icon className="h-3.5 w-3.5 text-gray-500" />}
                          onClick={() => {
                            setSelectedSource(source.name);
                            if (selectedAttribute !== "all") {
                              setSelectedAttribute("all");
                            }
                          }}
                          className={`text-xs ${selectedSource === source.name ? 'bg-gray-50 text-primary-600' : ''}`}
                        >
                          {source.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </div>
                <div className="relative">
                  <Menu position="bottom-start" offset={4} withinPortal={false}>
                    <Menu.Target>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<Type className="h-3 w-3" />}
                        className="text-xs font-medium flex items-center space-x-1"
                        rightSection={<ChevronDownIcon className="h-3 w-3" />}
                      >
                        <span>{listOfAttributes.find(item => item.name === selectedAttribute)?.label}</span>
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown className="p-1 min-w-[120px]">
                      {listOfAttributes.map((item) => (
                        <Menu.Item
                          key={item.name}
                          leftSection={<item.icon className="h-3.5 w-3.5 text-gray-500" />}
                          onClick={() => setSelectedAttribute(item.name)}
                          className={`text-xs ${selectedAttribute === item.name ? 'bg-gray-50 text-primary-600' : ''}`}
                        >
                          {item.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </div>
              </div>
            </div>
            
            {/* Active filters display */}
            {(selectedSource !== "all" || selectedAttribute !== "all") && (
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedSource !== "all" && (
                  <div className={`
                    text-xs px-2 py-0.5 rounded-md flex items-center 
                    ${selectedSource === 'native' 
                      ? 'bg-primary-50 text-primary-700 border border-primary-100' 
                      : selectedSource === 'segment' 
                        ? 'bg-blue-50 text-blue-700 border border-blue-100' 
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }
                  `}
                  >
                    {(() => {
                      const source = listOfSources.find(s => s.name === selectedSource);
                      const SourceIcon = source?.icon;
                      return (
                        <>
                          {SourceIcon && <SourceIcon className={`
                            h-3 w-3 mr-1 
                            ${selectedSource === 'native' 
                              ? 'text-primary-500' 
                              : selectedSource === 'segment' 
                                ? 'text-blue-500' 
                                : 'text-gray-500'
                            }
                          `}
                          />}
                          <span className="mr-1">{source?.label}</span>
                        </>
                      );
                    })()} 
                    <button 
                      type="button" 
                      className={`
                        hover:opacity-80 
                        ${selectedSource === 'native' 
                          ? 'text-primary-500' 
                          : selectedSource === 'segment' 
                            ? 'text-blue-500' 
                            : 'text-gray-500'
                        }
                      `}
                      onClick={() => setSelectedSource("all")}
                    >
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
                {selectedAttribute !== "all" && (
                  <div className={`
                    text-xs px-2 py-0.5 rounded-md flex items-center 
                    ${selectedAttribute === 'traits' 
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                      : selectedAttribute === 'metrics' 
                        ? 'bg-amber-50 text-amber-700 border border-amber-100' 
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }
                  `}
                  >
                    {(() => {
                      const attribute = listOfAttributes.find(a => a.name === selectedAttribute);
                      const AttributeIcon = attribute?.icon;
                      return (
                        <>
                          {AttributeIcon && <AttributeIcon className={`
                            h-3 w-3 mr-1 
                            ${selectedAttribute === 'traits' 
                              ? 'text-emerald-500' 
                              : selectedAttribute === 'metrics' 
                                ? 'text-amber-500' 
                                : 'text-gray-500'
                            }
                          `}
                          />}
                          <span className="mr-1">{attribute?.label}</span>
                        </>
                      );
                    })()}
                    <button 
                      type="button" 
                      className={`
                        hover:opacity-80 
                        ${selectedAttribute === 'traits' 
                          ? 'text-emerald-500' 
                          : selectedAttribute === 'metrics' 
                            ? 'text-amber-500' 
                            : 'text-gray-500'
                        }
                      `}
                      onClick={() => setSelectedAttribute("all")}
                    >
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
            {/* Results list */}
            <div className="flex-1 overflow-y-auto">
              {queriedData.length ? queriedData.map((item: any) => (
                <div
                  key={uuid()}
                  className={classNames(
                    "hover:bg-gray-200 transition-colors duration-100 group text-gray-700",
                    "block w-full relative cursor-pointer select-none py-1.5 pl-3 pr-8 border-b border-gray-100 last:border-b-0",
                    item === selected ? "bg-gray-50" : ""
                  )}
                >
                  <Checkbox
                    label={item.display_name}
                    id={uuid()}
                    size="xs"
                    radius="lg"
                    color="main.5" 
                    className="w-full"
                    onChange={() => setSelected(item)}
                    checked={selected.includes(item.name)}
                  />
                </div>
              )) : (
                <Paper className="w-full bg-gray-100 h-full flex justify-center items-center">
                  <Text size="sm" color="text.3" className="font-semibold text-center italic">No Matching Items</Text>
                </Paper>
              )}
            </div>
          </div>
      </Menu.Dropdown>
    </Menu>
  );
};

export default MultiSelectDropdown;
